import { isNativeIos, isUwp, isLegacyMunin } from "./envDetection";

type OperatingSystem = "ios" | "android" | "windows" | "web";

type PlatformOptions<T> = {
  android?: T;
  ios?: T;
  windows?: T;
  web?: T;
  default?: T;
};

type PlatformType = {
  /**
   * Determine the current environment's native operating system.
   * Possible values are `ios`, `android`, `windows`, or `web`.
   */
  OS: "ios" | "android" | "windows" | "web";

  /**
   * Select a value depending on the current operating environment.
   *
   * ```javascript
   * const result = Platform.select({
   *   ios: "if env is ios, will return this value",
   *   android: "if env is android...",
   *   windows: "if env is windows...",
   *   web: "if env is web...",
   *   default: "fallback...",
   * });
   * ```
   *
   * @param options A map of OS names to values
   */
  select: <T>(options: PlatformOptions<T>) => T | void;
};

// detect the environment we're running in
export const detectOS = (): OperatingSystem => {
  if (isNativeIos()) return "ios";
  if (isUwp()) return "windows";
  if (isLegacyMunin()) return "android";
  return "web";
};

// given a map of environment types to options, return the option for the current environment
export const selectByOS = <T>(
  platformOptions: PlatformOptions<T>,
  operatingSystem: OperatingSystem
): T | void => {
  if (!platformOptions || !operatingSystem) {
    return;
  }

  // return the platform option if it exists for the current environment
  // otherwise return a default value if it exists
  // and finally if all else fails, return undefined
  if (operatingSystem in platformOptions) {
    return platformOptions[operatingSystem];
  } else if ("default" in platformOptions) {
    return platformOptions["default"];
  }
};

const createPlatform = (): PlatformType => {
  const operatingSystem = detectOS();

  // uses the current environment to pick the right option from the platformOptions map
  const select = <T>(platformOptions: PlatformOptions<T>): T | void => {
    return selectByOS(platformOptions, operatingSystem);
  };

  return {
    OS: operatingSystem,
    select,
  };
};

/**
 * A utility that helps with cross-platform OS-level information.
 */
export const Platform = createPlatform();
